// src/data/mock/press.ts

/**
 * Press & Media Data Structure
 * 
 * Media coverage, press releases, and news articles about Champions Academy.
 * 
 * @note Replace placeholder data with actual press coverage
 * @note All images should be placed in /public/images/press/ directory
 */

export interface PressItem {
  id: string;
  
  // Article title
  title: {
    en: string;
    ar: string;
  };
  
  // Publication/Source name
  publication: string;
  
  // Source URL
  url?: string;
  
  // Publication logo
  logo?: string;
  
  // Article excerpt/summary
  excerpt: {
    en: string;
    ar: string;
  };
  
  // Full content (optional)
  content?: {
    en: string;
    ar: string;
  };
  
  // Publication date
  date: string;
  
  // Featured image
  image: string;
  
  // Additional images gallery
  gallery?: string[];
  
  // Type of media
  type: 'article' | 'video' | 'interview' | 'press-release' | 'feature' | 'documentary';
  
  // Category
  category?: 'achievement' | 'partnership' | 'event' | 'profile' | 'news' | 'opinion';
  
  // Author name
  author?: {
    en: string;
    ar: string;
  };
  
  // Language of original article
  language?: 'en' | 'ar' | 'both';
  
  // Video URL (for video type)
  videoUrl?: string;
  
  // Tags for filtering
  tags?: string[];
  
  // Is this a featured article?
  featured?: boolean;
  
  // External or internal press release
  source: 'external' | 'internal';
  
  // Views count (optional)
  views?: number;
  
  // Related press items
  relatedIds?: string[];
}

/**
 * Press Items Data
 * 
 * TODO: Replace with actual press coverage
 * - Add real articles and media mentions
 * - Update images and URLs
 * - Verify publication dates
 * - Add video links
 */
export const press: PressItem[] = [
  // ==========================================
  // FEATURED PRESS - أبرز التغطيات
  // ==========================================
  {
    id: "press-featured-001",
    featured: true,
    source: "external",
    type: "feature",
    category: "profile",
    title: {
      en: "Champions Academy: Building Dreams Against All Odds in Gaza",
      ar: "أكاديمية تشامبيونز: بناء الأحلام رغم كل الصعاب في غزة"
    },
    publication: "Al Jazeera English",
    logo: "/assets/partners/s-blob-v1-IMAGE-i1sQVoQj21A.png",
    url: "https://aljazeera.com",
    date: "2023-05-20",
    image: "/assets/images/s-blob-v1-IMAGE-feT8ETn1U54.jpeg",
    gallery: [
      "/assets/images/s-blob-v1-IMAGE-9MjiX0xpxqY.jpeg",
      "/assets/images/s-blob-v1-IMAGE-gwHF0h6PBSA.jpeg",
      "/assets/images/s-blob-v1-IMAGE-K7rmbrfAKoo.jpeg"
    ],
    author: {
      en: "Sarah Johnson",
      ar: "سارة جونسون"
    },
    language: "both",
    excerpt: {
      en: "A comprehensive look inside Gaza's premier multi-sport academy, where young athletes train with world-class methodology despite challenging circumstances. Champions Academy has become a beacon of hope, producing national champions and professional athletes while fostering community spirit and youth development.",
      ar: "نظرة شاملة داخل الأكاديمية الرياضية الأولى متعددة التخصصات في غزة، حيث يتدرب الرياضيون الشباب بمنهجية عالمية على الرغم من الظروف الصعبة. أصبحت أكاديمية تشامبيونز منارة أمل، تنتج أبطالاً وطنيين ورياضيين محترفين بينما تعزز روح المجتمع وتنمية الشباب."
    },
    tags: ["profile", "sports", "youth", "gaza", "inspiration"],
    views: 125000
  },

  // ==========================================
  // PARTNERSHIPS - الشراكات
  // ==========================================
  {
    id: "press-partnership-001",
    featured: true,
    source: "external",
    type: "article",
    category: "partnership",
    title: {
      en: "Real Madrid Foundation Announces Historic Partnership with Palestinian Sports Academy",
      ar: "مؤسسة ريال مدريد تعلن عن شراكة تاريخية مع أكاديمية رياضية فلسطينية"
    },
    publication: "ESPN",
    logo: "/assets/partners/s-blob-v1-IMAGE-myYp1R2xNe8.png",
    url: "https://espn.com",
    date: "2020-06-15",
    image: "/assets/images/s-blob-v1-IMAGE-HiJ0_LRWIVo.jpeg",
    gallery: [
      "/assets/images/s-blob-v1-IMAGE-fCIF9jo-oiw.jpeg"
    ],
    author: {
      en: "Miguel Rodriguez",
      ar: "ميغيل رودريغيز"
    },
    language: "en",
    excerpt: {
      en: "Real Madrid Foundation extends its global reach with a groundbreaking partnership to develop young football talent in Palestine. The collaboration brings world-class training methods, equipment, and coaching expertise to Champions Academy, opening doors for Palestinian youth to pursue professional football careers.",
      ar: "مؤسسة ريال مدريد توسع نطاقها العالمي بشراكة رائدة لتطوير مواهب كرة القدم الشابة في فلسطين. يجلب التعاون أساليب تدريب وأجهزة وخبرة تدريبية على مستوى عالمي إلى أكاديمية تشامبيونز، فاتحاً الأبواب للشباب الفلسطيني لمتابعة مسيرات كرة قدم احترافية."
    },
    tags: ["real-madrid", "partnership", "football", "international"],
    views: 250000
  },
  {
    id: "press-partnership-002",
    source: "external",
    type: "article",
    category: "partnership",
    title: {
      en: "UNICEF Champions Sports for Development Initiative in Gaza",
      ar: "اليونيسف تدعم مبادرة الرياضة من أجل التنمية في غزة"
    },
    publication: "UNICEF Press Centre",
    logo: "/assets/partners/s-blob-v1-IMAGE-pFxlrG7Kp48.png",
    url: "https://unicef.org",
    date: "2021-09-10",
    image: "/assets/images/s-blob-v1-IMAGE-IuZb2OMwJt0.jpeg",
    language: "both",
    excerpt: {
      en: "UNICEF announces support for Champions Academy's sports development programs, recognizing their impact on child protection, education, and wellbeing in Gaza. The partnership will expand access to quality sports training for vulnerable children.",
      ar: "اليونيسف تعلن دعمها لبرامج التنمية الرياضية في أكاديمية تشامبيونز، تقديراً لتأثيرها على حماية الطفل والتعليم والرفاهية في غزة. ستوسع الشراكة الوصول إلى تدريب رياضي عالي الجودة للأطفال الضعفاء."
    },
    tags: ["unicef", "partnership", "development", "children"],
    views: 85000
  },

  // ==========================================
  // DOCUMENTARIES - الأفلام الوثائقية
  // ==========================================
  {
    id: "press-documentary-001",
    featured: true,
    source: "external",
    type: "documentary",
    category: "profile",
    title: {
      en: "The Spirit of Sports: Champions Rising - Full Documentary",
      ar: "روح الرياضة: أبطال صاعدون - فيلم وثائقي كامل"
    },
    publication: "BBC Arabic",
    logo: "/assets/partners/s-blob-v1-IMAGE-7rbMBLqn8FA.png",
    url: "https://bbc.com/arabic",
    videoUrl: "https://youtube.com/watch?v=example",
    date: "2022-11-10",
    image: "/assets/images/s-blob-v1-IMAGE-GrqwWS_efRQ.jpeg",
    gallery: [
      "/assets/images/s-blob-v1-IMAGE-ggvobWIqLZc.jpeg",
      "/assets/images/s-blob-v1-IMAGE-wA5xAwin9fo.jpeg"
    ],
    author: {
      en: "Ahmad Khalil",
      ar: "أحمد خليل"
    },
    language: "both",
    excerpt: {
      en: "A moving 45-minute documentary showcasing how sports transforms lives and builds community in Gaza through Champions Academy. Follow the journeys of young athletes as they train, compete, and dream of professional careers while navigating daily challenges. Features exclusive interviews with coaches, parents, and success stories.",
      ar: "فيلم وثائقي مؤثر مدته 45 دقيقة يعرض كيف تحول الرياضة الحياة وتبني المجتمع في غزة من خلال أكاديمية تشامبيونز. تابع رحلات الرياضيين الشباب بينما يتدربون ويتنافسون ويحلمون بمسيرات احترافية بينما يتنقلون في التحديات اليومية. يتضمن مقابلات حصرية مع المدربين والأهالي وقصص النجاح."
    },
    tags: ["documentary", "bbc", "video", "inspirational"],
    views: 450000
  },

  // ==========================================
  // INTERVIEWS - المقابلات
  // ==========================================
  {
    id: "press-interview-001",
    source: "external",
    type: "interview",
    category: "profile",
    title: {
      en: "Interview: The Vision Behind Champions Academy - Building Athletes, Building Character",
      ar: "مقابلة: الرؤية وراء أكاديمية تشامبيونز - بناء الرياضيين، بناء الشخصية"
    },
    publication: "Palestine Sports TV",
    url: "https://palestinesports.tv",
    date: "2023-08-05",
    image: "/assets/images/s-blob-v1-IMAGE-56VzkL9pHSw.jpeg",
    language: "ar",
    excerpt: {
      en: "Exclusive 30-minute interview with Champions Academy leadership about their mission, achievements, and future plans. Discussing the academy's holistic approach to athlete development, partnerships with international organizations, and their impact on Palestinian sports.",
      ar: "مقابلة حصرية مدتها 30 دقيقة مع قيادة أكاديمية تشامبيونز حول مهمتهم وإنجازاتهم وخططهم المستقبلية. مناقشة النهج الشامل للأكاديمية لتطوير الرياضيين، الشراكات مع المنظمات الدولية، وتأثيرهم على الرياضة الفلسطينية."
    },
    tags: ["interview", "leadership", "vision", "development"],
    views: 65000
  },
  {
    id: "press-interview-002",
    source: "external",
    type: "interview",
    category: "achievement",
    title: {
      en: "From Gaza to Glory: Interview with Professional Footballer Mohamed Al-Khalidi",
      ar: "من غزة إلى المجد: مقابلة مع لاعب كرة القدم المحترف محمد الخالدي"
    },
    publication: "Goal.com",
    logo: "/assets/partners/s-blob-v1-IMAGE-pZzRsBGfRYg.png",
    url: "https://goal.com",
    date: "2023-10-15",
    image: "/assets/images/s-blob-v1-IMAGE-9MjiX0xpxqY.jpeg",
    language: "both",
    excerpt: {
      en: "Champions Academy alumnus Mohamed Al-Khalidi shares his incredible journey from training in Gaza to playing professional football in Egypt. He credits the academy's training and values for his success on and off the field.",
      ar: "خريج أكاديمية تشامبيونز محمد الخالدي يشارك رحلته المذهلة من التدريب في غزة إلى لعب كرة القدم الاحترافية في مصر. يُرجع الفضل لتدريب الأكاديمية وقيمها في نجاحه داخل وخارج الملعب."
    },
    tags: ["interview", "success-story", "football", "professional"],
    views: 120000
  },

  // ==========================================
  // ACHIEVEMENTS - الإنجازات
  // ==========================================
  {
    id: "press-achievement-001",
    source: "internal",
    type: "press-release",
    category: "achievement",
    title: {
      en: "50 Championships Strong: Champions Academy Celebrates Milestone Victory",
      ar: "50 بطولة قوية: أكاديمية تشامبيونز تحتفل بفوز تاريخي"
    },
    publication: "Champions Academy Media",
    date: "2023-12-15",
    image: "/assets/images/s-blob-v1-IMAGE-K7rmbrfAKoo.jpeg",
    gallery: [
      "/assets/images/s-blob-v1-IMAGE-gwHF0h6PBSA.jpeg",
      "/assets/images/s-blob-v1-IMAGE-3-Rt3ARYBgg.jpeg"
    ],
    language: "both",
    excerpt: {
      en: "Champions Academy celebrates its 50th championship win across various sports, marking a remarkable achievement in regional youth sports development. The milestone represents years of dedication, world-class training, and the talent of Palestinian youth athletes.",
      ar: "أكاديمية تشامبيونز تحتفل بفوزها بالبطولة الخمسين عبر مختلف الرياضات، مسجلة إنجازاً ملحوظاً في تطوير الرياضة الشبابية الإقليمية. يمثل هذا الإنجاز سنوات من التفاني والتدريب على مستوى عالمي وموهبة الرياضيين الشباب الفلسطينيين."
    },
    tags: ["achievement", "championship", "milestone", "celebration"],
    views: 95000
  },
  {
    id: "press-achievement-002",
    source: "external",
    type: "article",
    category: "achievement",
    title: {
      en: "Palestinian Swimmers Break National Records at Arab Championships",
      ar: "سباحون فلسطينيون يحطمون أرقاماً قياسية وطنية في البطولة العربية"
    },
    publication: "Arab Sports Journal",
    date: "2023-07-22",
    image: "/assets/images/s-blob-v1-IMAGE-wA5xAwin9fo.jpeg",
    language: "both",
    excerpt: {
      en: "Champions Academy swimmers shine at Arab Championships, breaking multiple national records. Three athletes from the academy's swimming program secured medals and set new Palestinian records in freestyle and butterfly events.",
      ar: "سباحو أكاديمية تشامبيونز يتألقون في البطولة العربية، محطمين أرقاماً قياسية وطنية متعددة. ثلاثة رياضيين من برنامج السباحة في الأكاديمية حصدوا ميداليات وسجلوا أرقاماً قياسية فلسطينية جديدة في فعاليات الحرة والفراشة."
    },
    tags: ["achievement", "swimming", "records", "championship"],
    views: 72000
  },

  // ==========================================
  // EVENTS - الفعاليات
  // ==========================================
  {
    id: "press-event-001",
    source: "internal",
    type: "press-release",
    category: "event",
    title: {
      en: "Champions Academy Hosts International Youth Sports Summit 2024",
      ar: "أكاديمية تشامبيونز تستضيف قمة الرياضة الشبابية الدولية 2024"
    },
    publication: "Champions Academy Media",
    date: "2024-03-10",
    image: "/assets/images/s-blob-v1-IMAGE-Myngy4tjQqo.jpeg",
    language: "both",
    excerpt: {
      en: "Champions Academy announces the inaugural International Youth Sports Summit, bringing together athletes, coaches, and sports development experts from across the region. The three-day event will feature workshops, competitions, and networking opportunities.",
      ar: "أكاديمية تشامبيونز تعلن عن افتتاح قمة الرياضة الشبابية الدولية، تجمع الرياضيين والمدربين وخبراء تطوير الرياضة من جميع أنحاء المنطقة. الحدث الذي يستمر ثلاثة أيام سيتضمن ورش عمل ومنافسات وفرص التواصل."
    },
    tags: ["event", "summit", "international", "youth"],
    views: 45000
  },

  // ==========================================
  // NEWS - الأخبار
  // ==========================================
  {
    id: "press-news-001",
    source: "external",
    type: "article",
    category: "news",
    title: {
      en: "New State-of-the-Art Training Facility Opens at Champions Academy",
      ar: "افتتاح منشأة تدريب حديثة في أكاديمية تشامبيونز"
    },
    publication: "Palestine Today",
    date: "2023-09-18",
    image: "/assets/images/s-blob-v1-IMAGE-N2M3FUp0KnU.jpeg",
    language: "both",
    excerpt: {
      en: "Champions Academy unveils new multi-million dollar training facility featuring Olympic-standard swimming pool, indoor basketball courts, and advanced fitness center. The expansion triples the academy's capacity to train young athletes.",
      ar: "أكاديمية تشامبيونز تكشف عن منشأة تدريب جديدة بقيمة عدة ملايين دولار تضم مسبحاً بمواصفات أولمبية وملاعب كرة سلة داخلية ومركز لياقة متقدم. يضاعف التوسع قدرة الأكاديمية على تدريب الرياضيين الشباب ثلاث مرات."
    },
    tags: ["news", "facility", "expansion", "infrastructure"],
    views: 110000
  },
  {
    id: "press-news-002",
    source: "external",
    type: "article",
    category: "news",
    title: {
      en: "Champions Academy Launches Girls-Only Football Program",
      ar: "أكاديمية تشامبيونز تطلق برنامج كرة قدم للفتيات فقط"
    },
    publication: "Women in Sport Middle East",
    date: "2023-04-25",
    image: "/assets/images/s-blob-v1-IMAGE-yvd-jbp4d9U.jpeg",
    language: "both",
    excerpt: {
      en: "Breaking barriers in Palestinian sports, Champions Academy launches dedicated girls' football program with female coaches and mentors. The initiative aims to empower young women through sports and create pathways to professional careers.",
      ar: "كسر الحواجز في الرياضة الفلسطينية، أكاديمية تشامبيونز تطلق برنامج كرة قدم مخصص للفتيات مع مدربات ومرشدات. تهدف المبادرة إلى تمكين الشابات من خلال الرياضة وخلق مسارات للمهن الاحترافية."
    },
    tags: ["news", "girls", "football", "empowerment"],
    views: 88000
  },

  // ==========================================
  // OPINION PIECES - المقالات الرأي
  // ==========================================
  {
    id: "press-opinion-001",
    source: "external",
    type: "article",
    category: "opinion",
    title: {
      en: "Why Sports Academies Matter: The Champions Academy Model",
      ar: "لماذا تهم الأكاديميات الرياضية: نموذج أكاديمية تشامبيونز"
    },
    publication: "Sports Development Review",
    date: "2023-06-30",
    image: "/assets/images/s-blob-v1-IMAGE-mwoEOLImAN8.jpeg",
    author: {
      en: "Dr. Omar Hassan",
      ar: "د. عمر حسن"
    },
    language: "en",
    excerpt: {
      en: "An analysis of how Champions Academy's holistic approach to youth sports development provides a replicable model for conflict-affected regions. The academy demonstrates that sports can be a powerful tool for social cohesion and youth empowerment.",
      ar: "تحليل لكيفية أن النهج الشامل لأكاديمية تشامبيونز لتطوير الرياضة الشبابية يوفر نموذجاً قابلاً للتكرار للمناطق المتأثرة بالنزاعات. تُظهر الأكاديمية أن الرياضة يمكن أن تكون أداة قوية للتماسك الاجتماعي وتمكين الشباب."
    },
    tags: ["opinion", "analysis", "development", "model"],
    views: 42000
  },

  // ==========================================
  // PLACEHOLDER
  // ==========================================
  {
    id: "press-placeholder-001",
    source: "external",
    type: "article",
    category: "news",
    title: {
      en: "Press Article Placeholder",
      ar: "مقالة صحفية مؤقتة"
    },
    publication: "Placeholder Publication",
    date: "2024-01-01",
    image: "/assets/images/s-blob-v1-IMAGE-3-Rt3ARYBgg.jpeg",
    language: "both",
    excerpt: {
      en: "Article excerpt placeholder - to be filled with actual press coverage.",
      ar: "مقتطف مقالة مؤقت - سيتم ملؤه بتغطية صحفية فعلية."
    },
    tags: ["placeholder"]
  }
];

/**
 * Media Partners - logos only
 */
export const mediaPartners = [
  { name: "Al Jazeera", logo: "/assets/partners/s-blob-v1-IMAGE-i1sQVoQj21A.png" },
  { name: "BBC", logo: "/assets/partners/s-blob-v1-IMAGE-7rbMBLqn8FA.png" },
  { name: "ESPN", logo: "/assets/partners/s-blob-v1-IMAGE-myYp1R2xNe8.png" },
  { name: "UNICEF", logo: "/assets/partners/s-blob-v1-IMAGE-pFxlrG7Kp48.png" },
  { name: "Real Madrid", logo: "/assets/partners/s-blob-v1-IMAGE-AjMC5ekUDho.png" },
  { name: "FIFA", logo: "/assets/partners/s-blob-v1-IMAGE-Dr0h29pjjQY.png" },
];

/**
 * Utility Functions
 */

// Get press by type
export const getPressByType = (type: PressItem['type']): PressItem[] => {
  return press.filter(p => p.type === type);
};

// Get featured press
export const getFeaturedPress = (): PressItem[] => {
  return press.filter(p => p.featured);
};

// Get press by category
export const getPressByCategory = (category: string): PressItem[] => {
  return press.filter(p => p.category === category);
};

// Get press by source
export const getPressBySource = (source: 'external' | 'internal'): PressItem[] => {
  return press.filter(p => p.source === source);
};

// Get recent press (last 6 months)
export const getRecentPress = (): PressItem[] => {
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  return press.filter(p => new Date(p.date) >= sixMonthsAgo);
};

// Sort by date
export const sortByDate = (items: PressItem[], descending = true): PressItem[] => {
  return [...items].sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return descending ? dateB - dateA : dateA - dateB;
  });
};

// Get total views
export const getTotalViews = (): number => {
  return press.reduce((total, item) => total + (item.views || 0), 0);
};

/**
 * Statistics
 */
export const pressStats = {
  total: press.length,
  byType: {
    article: press.filter(p => p.type === 'article').length,
    video: press.filter(p => p.type === 'video').length,
    interview: press.filter(p => p.type === 'interview').length,
    pressRelease: press.filter(p => p.type === 'press-release').length,
    feature: press.filter(p => p.type === 'feature').length,
    documentary: press.filter(p => p.type === 'documentary').length,
  },
  bySource: {
    external: press.filter(p => p.source === 'external').length,
    internal: press.filter(p => p.source === 'internal').length,
  },
  featured: press.filter(p => p.featured).length,
  totalViews: getTotalViews(),
  mediaPartners: mediaPartners.length
};
